import { defineStore } from "pinia";
import { getProjectList, getUserInfo, setUserConfig } from "@/api/common";

interface tenant {
	id: string;
	tenantType: string;
}
interface userInfo {
	avatar: string;
	id: string;
	username: string;
	userType: string;
	name: string;
	tenantCode: string;
	tenant: tenant;
}

export type FontTheme = "modern" | "editorial" | "friendly";
const FONT_THEMES: FontTheme[] = ["modern", "editorial", "friendly"];
const DEFAULT_FONT_THEME: FontTheme = "modern";

function readFontTheme(): FontTheme {
	const stored = window.localStorage.getItem("fontTheme") as FontTheme | null;
	return stored && FONT_THEMES.includes(stored) ? stored : DEFAULT_FONT_THEME;
}

interface list {
	departmentName: string | null;
	hospitalName: string | null;
	projectVOs: any[];
	isCollapse: boolean;
	fcode: string;
	readonly: boolean;
	masking: boolean;
	compactForm: boolean;
	darkMode: boolean;
	fontTheme: FontTheme;
}

interface project {
	projectCode: string | null;
	projectId: string | null;
	projectName: string | null;
	subProject?: string | null;
}

interface user {
	userInfo: userInfo;
	isInfo: boolean;
}

export const useCommonStore = defineStore("common", {
	state: (): list => ({
		departmentName: window.localStorage.getItem("departmentName"),
		hospitalName: window.localStorage.getItem("hospitalName"),
		projectVOs: [],
		isCollapse:
			window.localStorage.getItem("isCollapse") === "true" ? true : false,
		fcode: "",
		readonly: false,
		masking: window.localStorage.getItem("masking") === "true",
		compactForm: window.localStorage.getItem("compactForm") === "true",
		darkMode: window.localStorage.getItem("darkMode") === "true",
		fontTheme: readFontTheme(),
	}),
	actions: {
		getInfo(fn: Function = (_data: any) => {}) {
			getProjectList()
				.then((result: any) => {
					const { departmentName, hospitalName, projectVOs } = result.data;
					this.departmentName = departmentName || "";
					this.hospitalName = hospitalName || "";
					this.projectVOs = projectVOs;
					window.localStorage.setItem("departmentName", departmentName || "");
					window.localStorage.setItem("hospitalName", hospitalName || "");
					fn(result.data);
				})
				.catch(() => {
					// Always invoke the callback so the caller can flip its `initialized`
					// flag and avoid a stuck loading / wrong empty-state UI when the API
					// errors (e.g. a transient 401 between login redirect and token
					// availability — the "no projects yet" flash that goes away on refresh).
					fn(null);
				});
		},
		changeCollapse() {
			this.isCollapse = !this.isCollapse;
			window.localStorage.setItem("isCollapse", this.isCollapse + "");
		},
		setFcode(fcode: string) {
			this.fcode = fcode;
		},
		setReadonly(readonly: boolean) {
			this.readonly = readonly;
		},
		toggleMasking() {
			this.masking = !this.masking;
			window.localStorage.setItem("masking", this.masking + "");
		},
		toggleCompactForm() {
			this.compactForm = !this.compactForm;
			window.localStorage.setItem("compactForm", this.compactForm + "");
		},
		toggleDarkMode() {
			this.darkMode = !this.darkMode;
			window.localStorage.setItem("darkMode", this.darkMode + "");
			this.applyDarkMode();
		},
		applyDarkMode() {
			const root = document.documentElement;
			if (this.darkMode) {
				root.classList.add("dark");
			} else {
				root.classList.remove("dark");
			}
		},
		setFontTheme(theme: FontTheme) {
			if (!FONT_THEMES.includes(theme)) return;
			this.fontTheme = theme;
			window.localStorage.setItem("fontTheme", theme);
			this.applyFontTheme();
		},
		applyFontTheme() {
			const body = document.body;
			FONT_THEMES.forEach((t) => body.classList.remove(`font-theme-${t}`));
			body.classList.add(`font-theme-${this.fontTheme}`);
		},
	},
});

export const useProjectStore = defineStore("project", {
	state: (): project => ({
		projectCode: window.localStorage.getItem("projectCode"),
		projectId: window.localStorage.getItem("projectId"),
		projectName: window.localStorage.getItem("projectName"),
		subProject: window.localStorage.getItem("subProject"),
	}),
	actions: {
		getProject() {
			this.projectCode = window.localStorage.getItem("projectCode") || "";
			this.projectId = window.localStorage.getItem("projectId") || "";
			this.projectName = window.localStorage.getItem("projectName") || "";
			this.subProject = window.localStorage.getItem("subProject") || "";
		},
		setProject(options: any) {
			window.localStorage.setItem("projectCode", options.projectCode);
			window.localStorage.setItem("projectId", options.projectId);
			window.localStorage.setItem("projectName", options.projectName);
			window.localStorage.setItem("subProject", options.subProject);
			this.projectCode = options.projectCode;
			this.projectId = options.projectId;
			this.projectName = options.projectName;
			this.subProject = options.subProject;
		},
	},
});

export const useUserStore = defineStore("user-info", {
	state: (): user => ({
		userInfo: {
			avatar: "",
			id: "",
			username: "",
			userType: "",
			name: "",
			tenantCode: "",
			tenant: {
				id: "",
				tenantType: "",
			},
		},
		isInfo: false,
	}),

	actions: {
		getInfo(fn: Function = (_data: any) => {}) {
			if (this.isInfo) {
				fn(this.userInfo);
			} else {
				getUserInfo()
					.then((result: any) => {
						this.userInfo = result.data;
						this.isInfo = true;
						fn(result.data);
					})
					.catch(() => {});
			}
		},
		setFlag(flag: boolean) {
			this.isInfo = flag;
		},
		setInfo(user: any, cb: Function = () => {}) {
			setUserConfig(user)
				.then(() => {
					this.setFlag(false);
					this.getInfo();
					if (cb) {
						cb();
					}
				})
				.catch(() => {});
		},
	},
});
